import React, { useCallback, useMemo, useState } from 'react'
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	MenuItem,
	Stack,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import AddIcon from '@mui/icons-material/Add'

import ImageDropzone from '../core/ImageDropzone'
import { ImageSlider } from '../core/ImageSlider'
import { useTokenClaims } from '../../security/useTokenClaims'
import { showErrorToast, showSuccessToast } from '../../services/CommonServices/alertServices'
import { listFormDefinitions, getFormDefinition } from '../../services/formDefinitionsService'
import type { FormDefinitionDetail, FormField } from '../../types/Form/FormDefinition'
import type { FormSubmissionRequest } from '../../types/Form/FormSubmission'
import { submitFormSubmission } from '../../services/formDefinitionsService'
import { createScaffoldRequest, searchTagNumber } from '../../services/scaffoldRequestService'
import {
	buildDynamicFormSections,
	buildRequiredFieldErrors,
	buildSubmissionValues,
	sortFormFieldOptions,
} from '../../utils/dynamicFormsUtils'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { usePermissionContext } from '../../security/PermissionContext'

const SCAFFOLD_REQUEST_CODE = 'scaffold_request'
const SCAFFOLD_ESTIMATION_CODE = 'scaffold_estimation'

type FieldValues = Record<string, string>
type FieldErrors = Record<string, string | undefined>

/** Tag number applies only when action is not Erect (and an action is selected). */
function shouldShowTagNumberField(values: FieldValues): boolean {
	const a = (values.action ?? '').trim()
	return a !== '' && a !== 'Erect'
}

interface Props {
	onSuccess?: () => void
}

export default function NewRequestDialog({ onSuccess }: Props) {
	const { projectId, id } = useTokenClaims()
	const { hasPermission } = usePermissionContext()
	const includeEstimationStep = hasPermission(79)
	const lastFormStep = includeEstimationStep ? 2 : 1
	const attachmentsStepIndex = includeEstimationStep ? 2 : 1

	const [open, setOpen] = useState(false)

	const [loading, setLoading] = useState(false)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [submitting, setSubmitting] = useState(false)

	const [requestForm, setRequestForm] = useState<FormDefinitionDetail | null>(null)
	const [estimationForm, setEstimationForm] = useState<FormDefinitionDetail | null>(null)

	const [requestValues, setRequestValues] = useState<FieldValues>({})
	const [estimationValues, setEstimationValues] = useState<FieldValues>({})
	const [requestErrors, setRequestErrors] = useState<FieldErrors>({})
	const [estimationErrors, setEstimationErrors] = useState<FieldErrors>({})

	const [attachments, setAttachments] = useState<File[]>([])
	const attachmentImages = attachments.filter((file) => !file.name.toLowerCase().endsWith('.pdf'))

	const handleAddAttachments = (files: File[]) => {
		setAttachments((prev) => [
			...prev,
			...files.filter(
				(file) =>
					!prev.some(
						(f) =>
							f.name === file.name && f.size === file.size && f.lastModified === file.lastModified,
					),
			),
		])
	}
	const [openAttachFiles, setOpenAttachFiles] = useState(false)

	const [activeStep, setActiveStep] = useState(0)

	const [tagNumberOptions, setTagNumberOptions] = useState<string[]>([])
	const [tagNumberLoading, setTagNumberLoading] = useState(false)
	const [tagNumberOpen, setTagNumberOpen] = useState(false)

	const searchTagNumbers = useCallback(
		async (searchText: string) => {
			if (!searchText.trim() || searchText.length < 2) {
				setTagNumberOptions([])
				return
			}
			if (!projectId) return
			try {
				setTagNumberLoading(true)
				const results = await searchTagNumber(searchText, Number(projectId))
				setTagNumberOptions(results.map((tag) => tag.toString()))
			} catch (error) {
				console.error('Error searching tag numbers:', error)
				setTagNumberOptions([])
			} finally {
				setTagNumberLoading(false)
			}
		},
		[projectId],
	)

	const debouncedSearchTagNumbers = useMemo(() => {
		let timeoutId: ReturnType<typeof setTimeout>
		return (searchText: string) => {
			clearTimeout(timeoutId)
			timeoutId = setTimeout(() => searchTagNumbers(searchText), 300)
		}
	}, [searchTagNumbers])

	const resetLocalState = () => {
		setRequestValues({})
		setEstimationValues({})
		setRequestErrors({})
		setEstimationErrors({})
		setAttachments([])
		setOpenAttachFiles(false)
		setActiveStep(0)
		setTagNumberOptions([])
		setTagNumberOpen(false)
		setTagNumberLoading(false)
	}

	const loadForms = useCallback(async () => {
		if (!projectId) return
		setLoading(true)
		setLoadError(null)
		try {
			const list = await listFormDefinitions()

			const requestSummary = list.find((f) => f.code === SCAFFOLD_REQUEST_CODE)
			if (!requestSummary) {
				setRequestForm(null)
				setEstimationForm(null)
				setLoadError(`Missing form configuration for ${SCAFFOLD_REQUEST_CODE}.`)
				return
			}

			const reqDetail = await getFormDefinition(requestSummary.id)
			setRequestForm(reqDetail)

			if (!includeEstimationStep) {
				setEstimationForm(null)
				return
			}

			const estimationSummary = list.find((f) => f.code === SCAFFOLD_ESTIMATION_CODE)
			if (!estimationSummary) {
				setEstimationForm(null)
				setLoadError(`Missing form configuration for ${SCAFFOLD_ESTIMATION_CODE}.`)
				return
			}

			const estDetail = await getFormDefinition(estimationSummary.id)
			setEstimationForm(estDetail)
		} catch {
			setRequestForm(null)
			setEstimationForm(null)
			setLoadError('Could not load form configuration.')
		} finally {
			setLoading(false)
		}
	}, [projectId, includeEstimationStep])

	const ensureDefaults = useCallback(
		(
			formDetail: FormDefinitionDetail | null,
			setValues: (fn: (p: FieldValues) => FieldValues) => void,
		) => {
			if (!formDetail?.fields?.length) return
			setValues((prev) => {
				const next = { ...prev }
				for (const f of formDetail.fields) {
					if (f.isHidden) continue
					if (next[f.fieldKey] === undefined) next[f.fieldKey] = ''
				}
				return next
			})
		},
		[],
	)

	React.useEffect(() => {
		if (!open) return
		resetLocalState()
		loadForms()
	}, [open, loadForms])

	React.useEffect(() => {
		ensureDefaults(requestForm, setRequestValues)
	}, [requestForm, ensureDefaults])

	React.useEffect(() => {
		ensureDefaults(estimationForm, setEstimationValues)
	}, [estimationForm, ensureDefaults])

	const requestSections = useMemo(
		() => (requestForm ? buildDynamicFormSections(requestForm) : []),
		[requestForm],
	)
	const estimationSections = useMemo(
		() => (estimationForm ? buildDynamicFormSections(estimationForm) : []),
		[estimationForm],
	)

	const setRequestField = (key: string, value: string) => {
		setRequestValues((p) => {
			const next = { ...p, [key]: value }
			if (key === 'action') {
				const a = value.trim()
				if (a === '' || a === 'Erect') {
					next.tagNumber = ''
				}
			}
			return next
		})
		if (key === 'action') {
			const a = value.trim()
			if (a === '' || a === 'Erect') {
				setTagNumberOptions([])
				setTagNumberOpen(false)
			}
		}
		setRequestErrors((p) => {
			const next = { ...p }
			if (next[key]) next[key] = undefined
			if (key === 'action') {
				const a = value.trim()
				if (a === '' || a === 'Erect') {
					next.tagNumber = undefined
				}
			}
			return next
		})
	}

	const setEstimationField = (key: string, value: string) => {
		setEstimationValues((p) => ({ ...p, [key]: value }))
		if (estimationErrors[key]) setEstimationErrors((p) => ({ ...p, [key]: undefined }))
	}

	const renderField = (
		f: FormField,
		values: FieldValues,
		errors: FieldErrors,
		setField: (key: string, value: string) => void,
	) => {
		const value = values[f.fieldKey] ?? ''

		const common = {
			label: f.label,
			size: 'small' as const,
			fullWidth: true,
			required: f.isRequired,
			helperText: errors[f.fieldKey] ?? f.helpText ?? undefined,
			placeholder: f.placeholder ?? undefined,
			error: Boolean(errors[f.fieldKey]),
		}

		const onTagNumberInput = (raw: string) => {
			setField('tagNumber', raw)
			debouncedSearchTagNumbers(raw)
			if (raw) {
				setTagNumberOpen(true)
			} else {
				setTagNumberOpen(false)
				setTagNumberOptions([])
			}
		}

		if (f.fieldKey === 'tagNumber') {
			return (
				<Autocomplete
					key={f.id}
					freeSolo
					disableClearable
					options={tagNumberOptions}
					value={value}
					onChange={(_, newValue) => onTagNumberInput(newValue != null ? String(newValue) : '')}
					onInputChange={(_, newInputValue) => onTagNumberInput(newInputValue)}
					open={tagNumberOpen}
					onOpen={() => setTagNumberOpen(true)}
					onClose={() => setTagNumberOpen(false)}
					loading={tagNumberLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={f.label}
							required={f.isRequired}
							error={Boolean(errors[f.fieldKey])}
							helperText={
								errors[f.fieldKey] ?? f.helpText ?? 'Enter the existing scaffold tag number'
							}
							variant='outlined'
							size='small'
							fullWidth
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{tagNumberLoading ? (
											<CircularProgress color='inherit' size={20} sx={{ m: 0, p: 0 }} />
										) : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
							sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
						/>
					)}
				/>
			)
		}

		if (f.fieldType === 'dropdown') {
			const opts = sortFormFieldOptions(f)

			return (
				<TextField
					key={f.id}
					select
					{...common}
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					InputLabelProps={{ shrink: true }}
					SelectProps={{ displayEmpty: !f.isRequired }}
				>
					{!f.isRequired && <MenuItem value=''>None</MenuItem>}
					{opts.length === 0 ? (
						<MenuItem value='' disabled>
							No options configured
						</MenuItem>
					) : (
						opts.map((o) => (
							<MenuItem key={o.id} value={o.value}>
								{o.value}
							</MenuItem>
						))
					)}
				</TextField>
			)
		}

		if (f.fieldType === 'number' || f.fieldType === 'currency') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='number'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					inputProps={f.fieldType === 'currency' ? { step: '0.01' } : { step: 'any' }}
				/>
			)
		}

		if (f.fieldType === 'date') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='date'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>
			)
		}

		const multiline =
			Boolean(f.placeholder && f.placeholder.length > 40) ||
			f.fieldKey.toLowerCase().includes('notes')

		return (
			<TextField
				key={f.id}
				{...common}
				value={value}
				onChange={(e) => setField(f.fieldKey, e.target.value)}
				multiline={multiline}
				minRows={multiline ? 3 : undefined}
			/>
		)
	}

	const renderSections = (
		title: string | undefined,
		sections: { sectionName: string; fields: FormField[] }[],
		values: FieldValues,
		errors: FieldErrors,
		setField: (key: string, value: string) => void,
		fieldFilter: (f: FormField) => boolean = () => true,
	) => {
		const visibleCount = sections.reduce((n, s) => n + s.fields.filter(fieldFilter).length, 0)
		return (
			<Box>
				{title ? (
					<Typography variant='subtitle1' sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
						{title}
					</Typography>
				) : null}
				{visibleCount === 0 ? (
					<Typography color='text.secondary'>This form has no visible fields.</Typography>
				) : (
					<Stack spacing={2}>
						{sections.map((s) => (
							<Box key={s.sectionName}>
								<Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
									{s.sectionName}
								</Typography>
								<Divider sx={{ mb: 1.5 }} />
								<Grid container spacing={2}>
									{s.fields.filter(fieldFilter).map((f) => (
										<Grid
											key={f.id}
											item
											xs={12}
											sm={f.fieldKey.toLowerCase().includes('notes') ? 12 : 6}
										>
											{renderField(f, values, errors, setField)}
										</Grid>
									))}
								</Grid>
							</Box>
						))}
					</Stack>
				)}
			</Box>
		)
	}

	const validate = (): boolean => {
		if (!requestForm) return false
		if (includeEstimationStep && !estimationForm) return false

		const reqErrors: FieldErrors = buildRequiredFieldErrors(requestForm, requestValues)
		delete reqErrors.tagNumber
		// Conditional: tagNumber required for Modify/Dismantle.
		const action = (requestValues.action ?? '').trim()
		if (action !== 'Erect' && !(requestValues.tagNumber ?? '').trim()) {
			reqErrors.tagNumber = 'Tag Number is required for Modify/Dismantle actions'
		}

		// Conditional: PO and Work Order cannot be the same.
		const po = (requestValues.po ?? '').trim()
		const wo = (requestValues.workOrder ?? '').trim()
		if (po && wo && po === wo) {
			reqErrors.po = 'Purchase Order and Work Order cannot be the same'
			reqErrors.workOrder = 'Purchase Order and Work Order cannot be the same'
		}

		setRequestErrors(reqErrors)

		if (!includeEstimationStep || !estimationForm) {
			setEstimationErrors({})
			return Object.keys(reqErrors).length === 0
		}

		const estErrors: FieldErrors = buildRequiredFieldErrors(estimationForm, estimationValues)
		for (const k of [
			'length',
			'width',
			'height',
			'estimatedDurationDays',
			'numberOfLegs',
			'numberOfDecks',
			'laborRequired',
		]) {
			if (estErrors[k]) continue
			const raw = (estimationValues[k] ?? '').trim()
			if (!raw) {
				estErrors[k] = 'This field is required'
			}
		}

		setEstimationErrors(estErrors)
		return Object.keys(reqErrors).length === 0 && Object.keys(estErrors).length === 0
	}

	const validateRequestStep = (): boolean => {
		if (!requestForm) return false
		const reqErrors: FieldErrors = buildRequiredFieldErrors(requestForm, requestValues)
		delete reqErrors.tagNumber
		// Conditional: tagNumber required for Modify/Dismantle.
		const action = (requestValues.action ?? '').trim()
		if (action !== 'Erect' && !(requestValues.tagNumber ?? '').trim()) {
			reqErrors.tagNumber = 'Tag Number is required for Modify/Dismantle actions'
		}

		// Conditional: PO and Work Order cannot be the same.
		const po = (requestValues.po ?? '').trim()
		const wo = (requestValues.workOrder ?? '').trim()
		if (po && wo && po === wo) {
			reqErrors.po = 'Purchase Order and Work Order cannot be the same'
			reqErrors.workOrder = 'Purchase Order and Work Order cannot be the same'
		}

		setRequestErrors(reqErrors)
		return Object.keys(reqErrors).length === 0
	}

	const validateEstimationStep = (): boolean => {
		if (!estimationForm) return false
		const estErrors: FieldErrors = buildRequiredFieldErrors(estimationForm, estimationValues)
		// Numeric required fields must be > 0 (matches EstimationForm.tsx UX).
		for (const k of [
			'length',
			'width',
			'height',
			'estimatedDurationDays',
			'numberOfLegs',
			'numberOfDecks',
			'laborRequired',
		]) {
			if (estErrors[k]) continue
			const raw = (estimationValues[k] ?? '').trim()
			if (!raw) {
				estErrors[k] = 'This field is required'
			}
		}
		setEstimationErrors(estErrors)
		return Object.keys(estErrors).length === 0
	}

	const validateCurrentStep = (step: number): boolean => {
		if (step === 0) return validateRequestStep()
		if (includeEstimationStep && step === 1) return validateEstimationStep()
		return true
	}

	const handleNext = () => {
		// Validate the currently visible form before advancing
		const ok = validateCurrentStep(activeStep)
		if (!ok) {
			showErrorToast({ message: 'Please fix errors before continuing.' })
			return
		}
		setActiveStep((s) => Math.min(lastFormStep, s + 1))
	}

	const handleSubmit = async () => {
		if (activeStep < lastFormStep) return
		if (!projectId) {
			showErrorToast({ message: 'Project ID is required' })
			return
		}
		if (!requestForm) return
		if (includeEstimationStep && !estimationForm) return
		if (!validate()) {
			showErrorToast({ message: 'Please go back and fill in all required fields.' })
			return
		}

		setSubmitting(true)
		try {
			// Ensure the date field is in YYYY-MM-DD format (input type="date" already provides this)
			if (
				(requestValues.requiredDate ?? '').trim() === '' &&
				requestValues.requiredDate !== undefined
			) {
				setRequestField('requiredDate', '')
			}

			let requestValuesPayload: FormSubmissionRequest['values'] = []
			let estimationValuesPayload: FormSubmissionRequest['values'] = []
			try {
				const requestValuesForSubmit = shouldShowTagNumberField(requestValues)
					? requestValues
					: { ...requestValues, tagNumber: '' }
				requestValuesPayload = buildSubmissionValues(requestForm, requestValuesForSubmit)
				if (includeEstimationStep && estimationForm) {
					estimationValuesPayload = buildSubmissionValues(estimationForm, estimationValues)
				}
			} catch (e: any) {
				showErrorToast({ message: e?.message || 'Invalid values.' })
				return
			}

			if (requestValuesPayload.length === 0) {
				showErrorToast({ message: 'Nothing to submit.' })
				return
			}
			if (includeEstimationStep && estimationValuesPayload.length === 0) {
				showErrorToast({ message: 'Nothing to submit.' })
				return
			}

			const requestSubmission: FormSubmissionRequest = {
				values: requestValuesPayload,
				specialFields: null,
			}

			const scaffoldReceipt = await createScaffoldRequest(
				projectId,
				requestForm.id,
				id,
				requestSubmission,
				attachments,
			)
			if (includeEstimationStep && estimationForm) {
				await submitFormSubmission(estimationForm.id, {
					values: estimationValuesPayload,
					specialFields: {
						scaffoldRequestId: scaffoldReceipt.id,
					},
				})
			}

			showSuccessToast(
				includeEstimationStep ? 'Forms submitted successfully!' : 'Request submitted successfully!',
				true,
			)
			setOpen(false)
			resetLocalState()
			onSuccess?.()
		} catch (e: any) {
			showErrorToast({ message: e?.message || 'Failed to submit. Please try again.' })
		} finally {
			setSubmitting(false)
		}
	}

	const handleClose = () => {
		setOpen(false)
		resetLocalState()
	}

	return (
		<>
			<Button
				variant='contained'
				onClick={() => setOpen(true)}
				sx={{
					minWidth: '200px',
					height: '40px',
				}}
				startIcon={<AddIcon />}
			>
				New Request
			</Button>

			<Dialog open={open} maxWidth='lg' fullWidth>
				<DialogTitle>Add Scaffold Request</DialogTitle>
				<DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
					{loading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={28} />
						</Box>
					)}

					{!loading && loadError && (
						<Alert severity='warning' sx={{ mb: 2 }}>
							{loadError}
						</Alert>
					)}

					{!loading && !loadError && requestForm && (!includeEstimationStep || estimationForm) && (
						<Stack spacing={2}>
							<Stepper activeStep={activeStep} sx={{ width: '100%', mb: 1 }}>
								<Step>
									<StepLabel>Request</StepLabel>
								</Step>
								{includeEstimationStep && (
									<Step>
										<StepLabel>Estimation</StepLabel>
									</Step>
								)}
								<Step>
									<StepLabel>Attachments</StepLabel>
								</Step>
							</Stepper>

							<Box sx={{ minHeight: 280 }}>
								{activeStep === 0 &&
									renderSections(
										undefined,
										requestSections,
										requestValues,
										requestErrors,
										setRequestField,
										(f) => f.fieldKey !== 'tagNumber' || shouldShowTagNumberField(requestValues),
									)}
								{includeEstimationStep &&
									activeStep === 1 &&
									renderSections(
										undefined,
										estimationSections,
										estimationValues,
										estimationErrors,
										setEstimationField,
									)}
								{activeStep === attachmentsStepIndex && (
									<Stack spacing={2}>
										<Typography variant='body2' color='text.secondary'>
											Add photos or PDFs if needed.
										</Typography>
										{!submitting && (
											<Box>
												<Button
													type='button'
													variant='outlined'
													color='primary'
													onClick={() => setOpenAttachFiles(true)}
													startIcon={<AttachFileIcon />}
													sx={{ color: 'text.primary' }}
												>
													Attach files
												</Button>
											</Box>
										)}
										{attachments.length === 0 ? (
											<Typography variant='body2' color='text.secondary'>
												No files attached.
											</Typography>
										) : (
											<Box>
												{attachmentImages.length > 0 && (
													<ImageSlider
														blobs={attachmentImages}
														handleDelete={(index) =>
															setAttachments((prev) =>
																prev.filter((f) => f !== attachmentImages[index]),
															)
														}
														readOnly={submitting}
													/>
												)}
												{attachments
													.filter((file) => file.name.toLowerCase().endsWith('.pdf'))
													.map((file) => (
														<Typography key={file.name} variant='body2'>
															• {file.name}
														</Typography>
													))}
											</Box>
										)}
									</Stack>
								)}
							</Box>
						</Stack>
					)}
				</DialogContent>

				<DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 2 }}>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							type='button'
							color='warning'
							variant='contained'
							onClick={handleClose}
							disabled={submitting}
						>
							Cancel
						</Button>
						<Button
							type='button'
							variant='outlined'
							onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
							disabled={submitting || loading || Boolean(loadError) || activeStep === 0}
							sx={{ color: 'primary.main' }}
						>
							Back
						</Button>
					</Box>
					<Box sx={{ display: 'flex', gap: 1 }}>
						{activeStep < lastFormStep ? (
							<Button
								type='button'
								variant='contained'
								color='primary'
								onClick={handleNext}
								disabled={
									submitting ||
									loading ||
									Boolean(loadError) ||
									!requestForm ||
									(includeEstimationStep && !estimationForm)
								}
							>
								Next
							</Button>
						) : (
							<Button
								type='button'
								variant='contained'
								color='primary'
								onClick={handleSubmit}
								disabled={
									submitting ||
									loading ||
									Boolean(loadError) ||
									!requestForm ||
									(includeEstimationStep && !estimationForm)
								}
							>
								{submitting ? 'Submitting...' : 'Submit'}
							</Button>
						)}
					</Box>
				</DialogActions>

				<ImageDropzone
					setFiles={handleAddAttachments}
					externalOpen={openAttachFiles}
					setExternalOpen={setOpenAttachFiles}
					additionalFilesTypes={['.pdf']}
				/>
			</Dialog>
		</>
	)
}
